import { Tabs } from 'antd';
import { ShieldAlert } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { sectionTrail } from '../../layouts/section-trail';
import { DocumentTypesPanel } from './DocumentTypesPanel';
import { REFERENCE_DATA_TAB_LABELS as LABELS, referenceDataTabPath, type ReferenceDataTab } from './reference-data-tabs';
import { JobTypesPanel } from './JobTypesPanel';
import { TrailerTypesPanel } from './TrailerTypesPanel';
import { TruckClassesPanel } from './TruckClassesPanel';
import { useDocumentTypes } from './use-document-types';
import { useJobTypes } from './use-job-types';
import { useRequiredDocumentTypes } from './use-required-document-types';
import { useTrailerTypes } from './use-trailer-types';
import { useTruckClasses } from './use-truck-classes';
import { useZoneKinds } from './use-zone-kinds';
import { useZones } from './use-zones';
import { ZoneKindsPanel } from './ZoneKindsPanel';
import { ZonesPanel } from './ZonesPanel';

function TruckClassesTab({ hook }: { hook: ReturnType<typeof useTruckClasses> }) {
  return (
    <TruckClassesPanel
      classes={hook.state.status === 'loaded' ? hook.state.classes : []}
      loading={hook.state.status === 'loading'}
      saveError={hook.saveError}
      saving={hook.saving}
      onCreate={hook.create}
      onUpdate={hook.update}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
      onResetSaveError={hook.resetSaveError}
    />
  );
}

function TrailerTypesTab({ hook }: { hook: ReturnType<typeof useTrailerTypes> }) {
  return (
    <TrailerTypesPanel
      types={hook.state.status === 'loaded' ? hook.state.types : []}
      loading={hook.state.status === 'loading'}
      saveError={hook.saveError}
      saving={hook.saving}
      onCreate={hook.create}
      onUpdate={hook.update}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
      onResetSaveError={hook.resetSaveError}
    />
  );
}

function DocumentTypesTab({ hook }: { hook: ReturnType<typeof useDocumentTypes> }) {
  return (
    <DocumentTypesPanel
      types={hook.state.status === 'loaded' ? hook.state.types : []}
      loading={hook.state.status === 'loading'}
      saveError={hook.saveError}
      saving={hook.saving}
      onCreate={hook.create}
      onUpdate={hook.update}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
      onResetSaveError={hook.resetSaveError}
    />
  );
}

function JobTypesTab({
  jobTypes,
  requiredDocumentTypes,
}: {
  jobTypes: ReturnType<typeof useJobTypes>;
  requiredDocumentTypes: ReturnType<typeof useRequiredDocumentTypes>;
}) {
  return (
    <JobTypesPanel
      types={jobTypes.state.status === 'loaded' ? jobTypes.state.types : []}
      loading={jobTypes.state.status === 'loading'}
      saveError={jobTypes.saveError}
      saving={jobTypes.saving}
      onCreate={jobTypes.create}
      onUpdate={jobTypes.update}
      onDeactivate={jobTypes.deactivate}
      onReactivate={jobTypes.reactivate}
      onResetSaveError={jobTypes.resetSaveError}
      requiredDocumentTypePanel={{
        types: requiredDocumentTypes.state.status === 'loaded' ? requiredDocumentTypes.state.types : [],
        loading: requiredDocumentTypes.state.status === 'loading',
        saveError: requiredDocumentTypes.saveError,
        saving: requiredDocumentTypes.saving,
        onCreate: requiredDocumentTypes.create,
        onUpdate: requiredDocumentTypes.update,
        onDeactivate: requiredDocumentTypes.deactivate,
        onReactivate: requiredDocumentTypes.reactivate,
        onResetSaveError: requiredDocumentTypes.resetSaveError,
      }}
    />
  );
}

function ZoneKindsTab({ hook }: { hook: ReturnType<typeof useZoneKinds> }) {
  return (
    <ZoneKindsPanel
      kinds={hook.state.status === 'loaded' ? hook.state.kinds : []}
      loading={hook.state.status === 'loading'}
      saveError={hook.saveError}
      saving={hook.saving}
      onCreate={hook.create}
      onUpdate={hook.update}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
      onResetSaveError={hook.resetSaveError}
    />
  );
}

function ZonesTab({ hook }: { hook: ReturnType<typeof useZones> }) {
  return (
    <ZonesPanel
      zones={hook.state.status === 'loaded' ? hook.state.zones : []}
      loading={hook.state.status === 'loading'}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
    />
  );
}

/**
 * Fleet and pricing vocabulary: truck classes, trailer types, document types,
 * job types, zone kinds and zones, in one tabbed screen — the shape later
 * reference-data milestones (check templates, service intervals) land in
 * too, rather than growing the Admin nav by one entry each.
 */
export function ReferenceDataPage({ tab }: { tab: ReferenceDataTab }) {
  const truckClasses = useTruckClasses();
  const trailerTypes = useTrailerTypes();
  const documentTypes = useDocumentTypes();
  const jobTypes = useJobTypes();
  const requiredDocumentTypes = useRequiredDocumentTypes();
  const zoneKinds = useZoneKinds();
  const zones = useZones();
  const navigate = useNavigate();
  const states = [truckClasses.state, trailerTypes.state, documentTypes.state, jobTypes.state, requiredDocumentTypes.state, zoneKinds.state, zones.state];

  if (states.some((s) => s.status === 'forbidden')) return <Navigate to="/" replace />;

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('reference-data')}
        title="Reference data"
        description="The fleet and pricing vocabulary trucks, trailers, documents, job types and rate lines are described with."
      />
      {states.some((s) => s.status === 'error') ? (
        <EmptyState
          icon={ShieldAlert}
          title="Couldn't load reference data"
          description="Something went wrong loading the reference-data catalogues. Try reloading the page."
        />
      ) : (
        <Tabs
          activeKey={tab}
          onChange={(key) => navigate(referenceDataTabPath(key as ReferenceDataTab))}
          items={[
            { key: 'truck-classes', label: LABELS['truck-classes'], children: <TruckClassesTab hook={truckClasses} /> },
            { key: 'trailer-types', label: LABELS['trailer-types'], children: <TrailerTypesTab hook={trailerTypes} /> },
            { key: 'document-types', label: LABELS['document-types'], children: <DocumentTypesTab hook={documentTypes} /> },
            { key: 'job-types', label: LABELS['job-types'], children: <JobTypesTab jobTypes={jobTypes} requiredDocumentTypes={requiredDocumentTypes} /> },
            { key: 'zone-kinds', label: LABELS['zone-kinds'], children: <ZoneKindsTab hook={zoneKinds} /> },
            { key: 'zones', label: LABELS.zones, children: <ZonesTab hook={zones} /> },
          ]}
        />
      )}
    </>
  );
}
